import { useMemo } from "react";
import type { NylasThread, NylasMessage } from "@equaliq/api-model-v2";
import type { AriInboxThread, AriInboxMessage, AriThreadLinkState } from "@/types/inbox.types";
import { useNylasConnections, useNylasThreads, useNylasMessages, useConnectEmail } from "./useNylas";
import { useNylasInboxStream } from "./useNylasInboxStream";

function initialsFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

function nameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

/**
 * Nylas passes through the provider's raw folder/label ids verbatim
 * (`INBOX`, `SENT`, `CATEGORY_PROMOTIONS`, `IMPORTANT`, ...). These are
 * mailbox-system folders, NOT deal tags — rendering them as chips shows
 * "Category Promotions" where a deal label belongs. Gmail/Nylas system
 * folder ids are reserved ALL-CAPS tokens (optionally `_`-joined); our own
 * deal labels are lowercase-hyphenated (`music-deal`). Suppress the ALL-CAPS
 * system ids so only genuine deal labels surface. Real deal-tag
 * categorization is backend work (Tier P) — until it lands, deal-linked
 * threads simply carry no chips rather than fake folder chips.
 */
function isSystemFolderLabel(label: string): boolean {
  // All-caps system folders (INBOX, UNREAD, CATEGORY_UPDATES, ...).
  if (/^[A-Z][A-Z0-9_]*$/.test(label)) return true;
  // Gmail's auto-generated opaque label IDs (Label_1, Label_42) — internal
  // identifiers, not human-readable categories, so they'd render as junk chips.
  if (/^Label_\d+$/i.test(label)) return true;
  return false;
}

function dealLabelsFromThread(thread: NylasThread): string[] {
  return (thread.labels ?? []).filter((label) => !isSystemFolderLabel(label));
}

/**
 * Maps a NylasThread (our enriched metadata from the DB) into an AriInboxThread
 * for the thread list. Messages are NOT loaded here — they load on click.
 */
function mapThreadToListItem(thread: NylasThread): AriInboxThread {
  const lastDate = new Date(thread.lastMessageAt);
  const today = new Date();
  const isToday = lastDate.toDateString() === today.toDateString();
  const timeStr = lastDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const dateStr = isToday
    ? "Today"
    : lastDate.toLocaleDateString(undefined, { month: "short", day: "numeric" });

  const email = thread.connectionEmail ?? "";

  // The api-server attaches the liveness projection as an untyped passthrough
  // on threads it linked to a DealThreadLink — the typed NylasThread shape
  // doesn't carry these, so read them through a boundary cast (Path A). The
  // backend projects a three-state `linkState` plus `dealId` (live only) /
  // `priorDealId` (in-org orphan only):
  //   • live      → dealId present → DealRoomView's real-data branch.
  //   • orphaned  → priorDealId present → recovery panel (reprocess/reconnect).
  //   • cross-org → neither id present → reconnect-only recovery panel.
  // R1-a adds the `unlinked` state: a thread the categorizer never linked to a
  // deal, surfaced ONLY in the "Needs review / All mail" view. The api-server
  // decorates it with the categorizer verdict/reason (from the
  // categorized_threads row R0-b persists), so the FE can explain why the deal
  // pipeline passed on it + offer the R1-b "Process into deal" action.
  const projected = thread as NylasThread & {
    dealId?: string;
    linkState?: AriThreadLinkState;
    priorDealId?: string;
    categorizerVerdict?: string;
    ignoreReason?: string;
    categorizerConfidence?: number;
    hasAttachments?: boolean;
    unread?: boolean;
    dealType?: string;
  };
  const dealId = projected.dealId;
  const linkState = projected.linkState;
  const priorDealId = projected.priorDealId;
  const categorizerVerdict = projected.categorizerVerdict;
  const ignoreReason = projected.ignoreReason;
  const categorizerConfidence = projected.categorizerConfidence;
  const hasAttachments = projected.hasAttachments;
  // Real thread-level read-state from the api-server's Nylas projection. Falls
  // back to the old `!analyzed` proxy only when the field is absent (mixed-
  // version window before the api-server deploy lands) so the row never crashes.
  const unread = projected.unread ?? !thread.analyzed;
  // Engine-classified contract type (recording/publishing/management/...),
  // projected by the api-server from analysisData.DEAL_TYPE. Prepended to
  // dealTags so it renders first as a chip via the existing AriTag (which
  // title-cases unknown ids → "Recording"). Fulfills the Tier P backend
  // reservation in isSystemFolderLabel's comment above. Absent until the
  // deal's analysis completes (or the backfill runs) → no chip, honest empty.
  const dealType = projected.dealType;

  return {
    id: thread.threadMetadataId,
    externalThreadId: thread.externalThreadId,
    ...(dealId ? { dealId } : {}),
    ...(linkState ? { linkState } : {}),
    ...(priorDealId ? { priorDealId } : {}),
    ...(categorizerVerdict ? { categorizerVerdict } : {}),
    ...(ignoreReason ? { ignoreReason } : {}),
    ...(categorizerConfidence !== undefined ? { categorizerConfidence } : {}),
    unread,
    starred: false,
    from: {
      name: nameFromEmail(email),
      email,
      initials: initialsFromEmail(email),
    },
    subject: thread.summary ?? `Thread ${thread.externalThreadId.slice(0, 12)}...`,
    date: dateStr,
    time: timeStr,
    dealTags: [...(dealType ? [dealType] : []), ...dealLabelsFromThread(thread)],
    clientTags: [],
    keywords: thread.ragKeywords ?? [],
    messages: [],
    pipeline: { stage: "Prospecting", progress: 1 },
    ariAlerts: [],
    attachments: [],
    ...(hasAttachments ? { hasAttachments } : {}),
    aiSummary: thread.summary ?? "",
  };
}

/**
 * Maps NylasMessages into AriInboxMessages for the reading pane.
 */
export function mapNylasMessages(
  messages: NylasMessage[],
  connectionEmail: string
): AriInboxMessage[] {
  return messages
    .sort((a, b) => a.date - b.date)
    .map((msg) => {
      const senderParticipant = msg.from?.[0];
      const senderEmail = senderParticipant?.email ?? "unknown@unknown.com";
      const senderName = senderParticipant?.name || nameFromEmail(senderEmail);
      const isCurrentUser = senderEmail.toLowerCase() === connectionEmail.toLowerCase();

      const date = new Date(msg.date * 1000);
      const dateStr = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

      // Nylas messages carry a full HTML `body` (the email's real markup +
      // its own CSS) AND a clean plaintext `snippet`. The old path ran the
      // HTML through a crude /<[^>]*>/g strip, which leaves `<style>` block
      // contents + HTML entities behind as literal text — raw CSS dumped into
      // the reading pane. Stop stripping: hand the HTML through verbatim as
      // `bodyHtml` (the component sanitizes + sandboxes it in an <iframe>) and
      // keep a real plaintext value for `body`/`preview` (the snippet, or a
      // last-resort strip when there is no snippet). When the message is
      // plaintext-only (`msg.body` has no markup), `bodyHtml` stays undefined
      // and the component renders the plaintext path unchanged.
      const htmlBody = msg.body ?? "";
      const looksLikeHtml = /<[a-z!][\s\S]*>/i.test(htmlBody);
      const plainSnippet = (msg.snippet ?? "").trim();
      const plainBody = plainSnippet || htmlBody.replace(/<[^>]*>/g, "").trim();

      return {
        id: msg.id,
        sender: senderName,
        email: senderEmail,
        date: dateStr,
        body: plainBody,
        ...(looksLikeHtml ? { bodyHtml: htmlBody } : {}),
        preview: plainBody.slice(0, 120),
        isCurrentUser,
      };
    });
}

/**
 * Hook that provides AriInboxThread[] from Nylas backend for the thread list.
 * Does NOT load message content — that's fetched on thread selection via useSelectedThreadMessages.
 */
export function useAriInboxThreads(
  options: { enabled?: boolean; includeUnlinked?: boolean } = {},
) {
  // includeUnlinked — R1-a escape hatch, DEFAULT OFF. When the page's "Needs
  // review / All mail" toggle is on, the hook fetches the LEFT-joined list so
  // unlinked (categorizer-ignored) threads surface alongside deal-linked ones.
  // Off → the deal-only inbox is byte-for-byte unchanged.
  const { enabled = true, includeUnlinked = false } = options;
  const {
    connections,
    isLoading: connectionsLoading,
    refetch: refetchConnections,
  } = useNylasConnections({ enabled });
  const { connectEmail, isConnecting } = useConnectEmail();

  const connectionId = connections[0]?.connectionId ?? null;
  const connectionEmail = connections[0]?.email ?? "";

  const {
    threads: rawThreads,
    isLoading: threadsLoading,
    loadMore,
    nextToken,
    isLoadingMore,
    reauthRequired,
    refetch: refetchThreads,
  } = useNylasThreads(connectionId, includeUnlinked);

  // The inbox SSE stream is opened ONCE at the app shell by
  // useInboxStreamProvider (single owner, 004_state.txt:217-218). This hook no
  // longer opens its own EventSource — it reads the SWR caches the shell
  // provider keeps warm. Opening a second stream here would create a duplicate
  // /sse/inbox connection.
  useNylasInboxStream(connectionId, { enabled: !!connectionId });

  const threads: AriInboxThread[] = useMemo(
    () => rawThreads.map(mapThreadToListItem),
    [rawThreads]
  );

  return {
    threads,
    isLoading: connectionsLoading || threadsLoading,
    connected: connections.length > 0,
    connections,
    connectionId,
    connectionEmail,
    connectEmail,
    isConnecting,
    refetchConnections,
    // Revalidate the thread list (the liveness projection re-runs server-side).
    // Tier C recovery actions (reprocess/reconnect/delete) call this so a thread
    // that flipped orphaned→live (reconnect) or whose deal vanished (delete)
    // re-renders with its fresh linkState without a full page reload.
    refetchThreads,
    loadMore,
    hasMore: !!nextToken,
    isLoadingMore,
    reauthRequired,
  };
}

/**
 * Hook to load messages for a selected thread (on click).
 * Returns AriInboxMessage[] for the reading pane.
 */
export function useSelectedThreadMessages(
  connectionId: string | null,
  externalThreadId: string | null,
  connectionEmail: string
) {
  const { messages: rawMessages, isLoading } = useNylasMessages(
    externalThreadId ? connectionId : null
  );

  const messages = useMemo(() => {
    if (!externalThreadId || !rawMessages.length) return [];
    const threadMsgs = rawMessages.filter((m) => m.threadId === externalThreadId);
    return mapNylasMessages(threadMsgs, connectionEmail);
  }, [rawMessages, externalThreadId, connectionEmail]);

  return { messages, isLoading };
}
